import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Calendar, Globe, ChevronDown, MapPin, Mail } from "lucide-react";
import { CLINIC } from "@/lib/data";
import { useLang } from "@/lib/i18n";

export default function Header({ onBookClick }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const loc = useLocation();
  const { lang, setLang, t } = useLang();

  const NAV = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/doctors", label: t("nav.doctors") },
    { to: "/opd", label: t("nav.opd") },
    { to: "/services", label: t("nav.facility") },
    { to: "/contact", label: t("nav.contact") },
  ];

  useEffect(() => { setOpen(false); setLangOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const close = () => setLangOpen(false);
    if (langOpen) {
      window.addEventListener("click", close);
      return () => window.removeEventListener("click", close);
    }
  }, [langOpen]);

  return (
    <>
      {/* Top utility strip — refined, no NABL phrase */}
      <div className="hidden md:flex bg-deep text-white text-xs" data-testid="top-strip">
        <div className="max-w-7xl mx-auto px-6 w-full flex items-center justify-between py-2.5">
          <div className="flex items-center gap-5 text-white/80">
            <span className="inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-gold" /> {CLINIC.address}</span>
          </div>
          <div className="flex items-center gap-5">
            <a href={`tel:${CLINIC.phone}`} className="hover:text-gold transition-colors flex items-center gap-2" data-testid="top-phone">
              <Phone className="w-3.5 h-3.5" /> {CLINIC.phone}
            </a>
            <span className="opacity-30">|</span>
            <a href={`mailto:${CLINIC.email}`} className="hover:text-gold transition-colors flex items-center gap-2" data-testid="top-email">
              <Mail className="w-3.5 h-3.5" /> {CLINIC.email}
            </a>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 backdrop-blur-xl transition-all duration-300 ${
          scrolled ? "bg-white/95 shadow-soft border-b border-black/5" : "bg-white"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-24">
          <Link to="/" className="flex items-center gap-4" data-testid="logo-link">
            <div className="relative">
              <img src={CLINIC.logo} alt="Shri Anandpur Trust" className="h-16 w-auto" />
            </div>
            <div className="hidden sm:block leading-tight border-l border-black/10 pl-4">
              <div className="font-display text-deep text-[1.35rem] font-semibold tracking-tight leading-none">Shri Anandpur Trust</div>
              <div className="text-[10.5px] tracking-[0.22em] uppercase text-brand font-semibold mt-1.5">Charitable Diagnostic Centre</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5" data-testid="primary-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-[13px] font-semibold rounded-full transition-colors ${
                    isActive ? "text-brand" : "text-ink/75 hover:text-deep"
                  }`
                }
                data-testid={`nav-${item.to.replace('/', '') || 'home'}`}
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && <span className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-5 h-[3px] rounded-full bg-gold" />}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {/* Language switcher */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setLangOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold text-deep rounded-full hover:bg-deep/5 transition-colors"
                aria-label="Switch language"
                data-testid="lang-toggle"
              >
                <Globe className="w-4 h-4" strokeWidth={1.7} />
                <span className="uppercase">{lang}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${langOpen ? "rotate-180" : ""}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white shadow-lifted border border-black/5 rounded-2xl w-44 overflow-hidden z-50" data-testid="lang-menu">
                  {[
                    { code: "en", label: "English", flag: "🇬🇧" },
                    { code: "de", label: "Deutsch", flag: "🇩🇪" },
                  ].map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 hover:bg-brand/5 ${lang === l.code ? "bg-brand/5 text-brand font-semibold" : "text-deep"}`}
                      data-testid={`lang-${l.code}`}
                    >
                      <span className="text-base">{l.flag}</span>
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href={`tel:${CLINIC.phone}`}
              className="hidden xl:inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-deep border border-deep/15 rounded-full hover:bg-deep/5 transition-colors"
              data-testid="header-call-btn"
            >
              <Phone className="w-4 h-4" /> {t("cta.call")}
            </a>
            <button
              onClick={onBookClick}
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-brand text-white rounded-full hover:bg-brand-600 shadow-soft transition-all"
              data-testid="header-book-btn"
            >
              <Calendar className="w-4 h-4" /> {t("cta.book")}
            </button>
            <button
              className="lg:hidden p-2 rounded-full hover:bg-brand/5 text-deep"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-deep/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute right-0 top-0 h-full w-[88%] max-w-sm bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex flex-col items-center gap-3 text-center">
                <img src={CLINIC.logo} alt="SAT" className="h-12" />
                <div>
                  <div className="font-display text-deep text-base font-semibold leading-none">Shri Anandpur Trust</div>
                  <div className="text-[9px] tracking-[0.22em] uppercase text-brand font-semibold mt-1">Charitable Diagnostic</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-brand/5" data-testid="mobile-menu-close" aria-label="Close menu">
                <X className="w-6 h-6 text-deep" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `flex items-center justify-between px-6 py-4 text-base font-medium border-l-4 ${
                      isActive ? "border-gold text-brand bg-brand/5" : "border-transparent text-ink/80"
                    }`
                  }
                  data-testid={`mobile-nav-${item.to.replace('/', '') || 'home'}`}
                >
                  {item.label}
                  <span className="text-ink/30">→</span>
                </NavLink>
              ))}
              {/* Lang switch */}
              <div className="px-6 py-4 mt-2 border-t border-black/5">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-ink-soft mb-3">{t("lang.label")}</div>
                <div className="flex gap-2">
                  {[
                    { code: "en", label: "EN" },
                    { code: "de", label: "DE" },
                  ].map((l) => (
                    <button
                      key={l.code}
                      onClick={() => setLang(l.code)}
                      className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${lang === l.code ? "bg-deep text-white" : "bg-deep/5 text-deep"}`}
                      data-testid={`mobile-lang-${l.code}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            </nav>
            <div className="p-5 border-t bg-background/50 space-y-3">
              <button
                onClick={() => { setOpen(false); onBookClick?.(); }}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-brand text-white rounded-full font-medium"
                data-testid="mobile-book-btn"
              >
                <Calendar className="w-4 h-4" /> {t("cta.book")}
              </button>
              <a
                href={`tel:${CLINIC.phone}`}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 border border-deep/15 text-deep rounded-full font-medium"
                data-testid="mobile-call-btn"
              >
                <Phone className="w-4 h-4" /> {CLINIC.phone}
              </a>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
